import type { AbstractAnalyticsDispatcher } from "@asurion-core/eventualize";

import { removeMdnFormatting } from "@expert/common-ui";
import { useVerifiedCustomerStore } from "@expert/sales-tools";
import { isEmail, useForm } from "@mantine/form";
import { useDebouncedCallback } from "@mantine/hooks";

import { isBlocklisted } from "@/customer-panel/components/customer-verification/utils/isBlocklisted";
import { DEFAULT_COUNTRY_CODE, removeCountryCode, validatePhoneNumber } from "@/shared-utils";

import { maskToRegex } from "../utils/maskToRegex";

export type VerificationType = "phoneNumber" | "billingAccountNumberAndPin";

export interface VerificationFormValues {
    billingAccountNumber: string;
    pin: string;
}

export interface AdditionalDetailsFormValues {
    email: string;
    orderId: string;
    mdn: string;
}

export interface UseCustomerVerificationFormParams {
    sessionId: string;
    verificationType: VerificationType;
    initialMdn: string;
    initialEmail: string;
    initialOrderId: string;
    initialAccountNumber: string;
    showAdditionalDetailsEmailInput: boolean;
    showAdditionalDetailsOrderIdInput: boolean;
    showAdditionalDetailsPhoneNumberInput: boolean;
    orderIdMask: string | undefined;
    blocklistedNumbers: string[] | undefined;
    blocklistedPrefixes: string[] | undefined;
    dispatcher: AbstractAnalyticsDispatcher | undefined;
    shouldSkipMdnValidation: boolean | undefined;
}

export interface UseCustomerVerificationFormReturn {
    verification: {
        values: VerificationFormValues;
        getInputProps: ReturnType<typeof useForm<VerificationFormValues>>["getInputProps"];
        isValid: (field?: keyof VerificationFormValues) => boolean;
        isDirty: (field?: keyof VerificationFormValues) => boolean;
        getValues: () => VerificationFormValues;
        setFieldValue: (field: keyof VerificationFormValues, value: string) => void;
        key: (field: keyof VerificationFormValues) => string;
    };

    additionalDetails: {
        values: AdditionalDetailsFormValues;
        getInputProps: ReturnType<typeof useForm<AdditionalDetailsFormValues>>["getInputProps"];
        isValid: () => boolean;
        getValues: () => AdditionalDetailsFormValues;
        setFieldValue: (field: keyof AdditionalDetailsFormValues, value: string) => void;
        key: (field: keyof AdditionalDetailsFormValues) => string;
    };

    flushDebouncedUpdates: () => void;
    isFormsValid: () => boolean;
}

export function useCustomerVerificationForm(
    params: UseCustomerVerificationFormParams,
): UseCustomerVerificationFormReturn {
    const {
        sessionId,
        verificationType,
        initialMdn,
        initialEmail,
        initialOrderId,
        initialAccountNumber,
        showAdditionalDetailsEmailInput,
        showAdditionalDetailsOrderIdInput,
        showAdditionalDetailsPhoneNumberInput,
        orderIdMask,
        blocklistedNumbers,
        blocklistedPrefixes,
        dispatcher,
        shouldSkipMdnValidation,
    } = params;

    const { setUnverifiedCustomerData } = useVerifiedCustomerStore.getState();

    const debouncedSetUnverifiedCustomerData = useDebouncedCallback(
        (sid: string, customerData: Record<string, unknown>) => {
            setUnverifiedCustomerData(sid, customerData);
        },
        { delay: 300, flushOnUnmount: true },
    );

    const verificationForm = useForm<VerificationFormValues>({
        validateInputOnBlur: true,
        initialValues: {
            billingAccountNumber: initialAccountNumber,
            pin: "",
        },
        validate: {
            billingAccountNumber: (value) => {
                if (verificationType === "billingAccountNumberAndPin") {
                    if (!/^(?:\d{13}|vze\d{10})$/.test(value)) {
                        return "Billing account number is required";
                    }
                }
                return null;
            },
            pin: (value) => {
                if (verificationType === "billingAccountNumberAndPin" && value.length > 0) {
                    if (!/^\d+$/.test(value)) {
                        return "PIN can only contain numbers";
                    }
                }
                return null;
            },
        },
    });

    const additionalDetailsForm = useForm<AdditionalDetailsFormValues>({
        validateInputOnBlur: true,
        initialValues: {
            email: initialEmail,
            orderId: initialOrderId,
            mdn: initialMdn,
        },
        validate: {
            email: (value) => {
                if (showAdditionalDetailsEmailInput) {
                    return isEmail("Invalid email")(value);
                }
                return null;
            },
            orderId: (value) => {
                if (showAdditionalDetailsOrderIdInput && value.length > 0) {
                    const validationRegex = orderIdMask ? maskToRegex(orderIdMask) : /^\d{3}-\d{7}-\d{7}$/;
                    if (!validationRegex.test(value)) {
                        return "Invalid order ID";
                    }
                }
                return null;
            },
            mdn: (value) => {
                if (showAdditionalDetailsPhoneNumberInput) {
                    if (isBlocklisted(value, blocklistedNumbers, blocklistedPrefixes)) {
                        void dispatcher?.dispatchBusinessEvent("BlocklistedMdnSubmitted");
                        return "This isn't the customer's phone number. Please ask for the right one.";
                    }

                    if (!validatePhoneNumber(value, DEFAULT_COUNTRY_CODE, shouldSkipMdnValidation).valid) {
                        return "Invalid phone number";
                    }
                }
                return null;
            },
        },
    });

    additionalDetailsForm.watch("email", ({ value, dirty }) => {
        if (dirty) {
            debouncedSetUnverifiedCustomerData(sessionId, { email: value });
        }
    });

    additionalDetailsForm.watch("orderId", ({ value, dirty }) => {
        if (dirty) {
            debouncedSetUnverifiedCustomerData(sessionId, { orderId: value });
        }
    });

    additionalDetailsForm.watch("mdn", ({ value, dirty }) => {
        if (dirty) {
            debouncedSetUnverifiedCustomerData(sessionId, { mdn: removeMdnFormatting(value) });
        }
    });

    return {
        verification: {
            values: verificationForm.values,
            getInputProps: verificationForm.getInputProps,
            isValid: verificationForm.isValid,
            isDirty: verificationForm.isDirty,
            getValues: verificationForm.getValues,
            setFieldValue: verificationForm.setFieldValue,
            key: verificationForm.key,
        },

        additionalDetails: {
            values: additionalDetailsForm.values,
            getInputProps: additionalDetailsForm.getInputProps,
            isValid: additionalDetailsForm.isValid,
            getValues: additionalDetailsForm.getValues,
            setFieldValue: additionalDetailsForm.setFieldValue,
            key: additionalDetailsForm.key,
        },

        flushDebouncedUpdates: () => debouncedSetUnverifiedCustomerData.flush(),
        isFormsValid: () => verificationForm.isValid() && additionalDetailsForm.isValid(),
    };
}

/**
 * Creates an onAccept handler for IMaskInput that properly updates the form
 * and strips formatting from the MDN value.
 */
export function createMdnAcceptHandler(setFieldValue: (field: "mdn", value: string) => void): (value: string) => void {
    return (value: string) => {
        setFieldValue("mdn", removeCountryCode(removeMdnFormatting(value)));
    };
}

/**
 * Creates an onAccept handler for IMaskInput orderId field.
 */
export function createOrderIdAcceptHandler(
    setFieldValue: (field: "orderId", value: string) => void,
): (value: string) => void {
    return (value: string) => {
        setFieldValue("orderId", value);
    };
}
